import { Fragment, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  Checkbox,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  MenuItem,
  Typography,
} from '@mui/material';
import {
  getTransportSimulation,
  getTransportSimulationHistory,
  importSimulationTransports,
  rerunTransportSimulation,
  retrySimulationNotification,
} from '../../../api/transport-simulationApi';
import { getApiErrorMessage } from '../../../api/mix-deliveriesApi';

import ShelfLifeOptions from './ShelfLifeOptions';
import ShelfLifeAcceptance from './ShelfLifeAcceptance';
import SimulationScopePanel, { BUCKETS } from './SimulationScopePanel';
import SimulationReportPanel from './SimulationReportPanel';
import ProductionPlanPanel from './ProductionPlanPanel';
import TransportStockPage from './TransportStockPage';

const ACTIONS = {
  MIX_PICKING_REQUIRED: 'Możliwa kompletacja mix',
  AREA_REVIEW: 'Potwierdź zatwierdzony obszar',
  TRANSPORT_REVIEW: 'Sprawdź obszar całego transportu',
  PRODUCTION_PLANNED: 'Produkcja zaplanowana przed załadunkiem',
  PRODUCTION_TIME_UNCONFIRMED: 'Produkcja — sprawdź godzinę załadunku',
  READY: 'Dostępny',
  MIX_ONLY: 'Tylko miks',
  MISSING_DATA: 'Brak danych',
  STOCK_SHORTAGE: 'Brak pełnych palet / stacków',
  SHELF_LIFE_SHORTAGE: 'Zapas nie spełnia terminu',
  STORAGE_LOCATION_CHANGE: 'Konieczna zmiana składu',
  AREA_TRANSFER: 'Przewóz AWH/TWH',
};
const DEFAULT_ACTIONS = Object.keys(ACTIONS).filter(
  (action) => action !== 'READY' && action !== 'MIX_ONLY',
);
const AREAS = { AWH: 'AWH', TWH: 'TWH', CONVEYOR: 'Transporter' };
const number = (value) =>
  new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 3 }).format(value);
const date = (value) =>
  value ? value.slice(0, 10).split('-').reverse().join('.') : '—';

export default function TransportSimulationPage({ userRole }) {
  const [activeTab, setActiveTab] = useState('simulation');
  const [importsVisited, setImportsVisited] = useState(false);
  const [file, setFile] = useState(null);
  const fileInput = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const [runId, setRunId] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [scopeBucket, setScopeBucket] = useState('');
  const [selectedActions, setSelectedActions] = useState(DEFAULT_ACTIONS);
  const [data, setData] = useState({ run: null, items: [], total: 0 });
  const [expanded, setExpanded] = useState(null);
  const [historyPage, setHistoryPage] = useState(0);
  const [history, setHistory] = useState({ items: [], total: 0 });
  const [historyError, setHistoryError] = useState('');
  const canWrite = ['ADMIN', 'KONTROLER'].includes(userRole);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    setExpanded(null);
    getTransportSimulation(runId, {
      page: page + 1,
      scopeBucket: scopeBucket || undefined,
      actions: selectedActions.join(','),
      limit,
      search: search || undefined,
    })
      .then((result) => {
        if (!active) return;
        const lastPage = Math.max(0, Math.ceil(result.total / limit) - 1);
        if (page > lastPage) {
          setPage(lastPage);
          return;
        }
        setData(result);
        // Przypinamy wynik, żeby nowe symulacje nie zmieniały kolejnych stron.
        if (!runId && result.run) setRunId(result.run.id);
      })
      .catch((err) => {
        if (active) setError(getApiErrorMessage(err));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [page, limit, search, scopeBucket, selectedActions, runId, refresh]);

  useEffect(() => {
    let active = true;
    setHistoryError('');
    getTransportSimulationHistory({ page: historyPage + 1, limit: 10 })
      .then((result) => {
        if (active) setHistory(result);
      })
      .catch((err) => {
        if (active) setHistoryError(getApiErrorMessage(err));
      });
    return () => {
      active = false;
    };
  }, [historyPage, refresh]);

  const calculate = async (rerun = false) => {
    setBusy(true);
    setError('');
    try {
      const result = rerun
        ? await rerunTransportSimulation(data.run.transportImportId)
        : await importSimulationTransports(file);
      setRunId(result.id);
      setActiveTab('simulation');
      setPage(0);
      setSearch('');
      setSearchInput('');
      setHistoryPage(0);
      setRefresh((value) => value + 1);
      if (!rerun) {
        setFile(null);
        if (fileInput.current) fileInput.current.value = '';
      }
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const retryMail = async () => {
    setBusy(true);
    setError('');
    try {
      await retrySimulationNotification(data.run.id);
      setRefresh((v) => v + 1);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const mailLabels = {
    SKIPPED: 'Pominięto (brak zmian lub wynik historyczny)',
    PENDING: 'Oczekuje na wysyłkę',
    SENDING: 'Wysyłka rozpoczęta — wynik może wymagać weryfikacji',
    SENT: 'Wysłano',
    FAILED: 'Nie udało się wysłać',
  };
  const run = data.run;
  return (
    <Stack spacing={2} sx={{ p: 3 }}>
      <Typography variant="h4">Symulacja dostępności pełnych palet</Typography>
      <Typography color="text.secondary">
        Transporty według daty załadunku. Zapas kontroli jakości jest
        przydzielany wyłącznie dostawom 360…
      </Typography>
      <Tabs
        value={activeTab}
        onChange={(_, value) => {
          setActiveTab(value);
          if (value === 'imports') setImportsVisited(true);
          else setRefresh((v) => v + 1);
        }}
        aria-label="Sekcje symulacji"
      >
        <Tab
          value="simulation"
          label="Symulacja"
          id="simulation-tab"
          aria-controls="simulation-panel"
        />
        <Tab
          value="imports"
          label="Importy"
          id="imports-tab"
          aria-controls="imports-panel"
        />
      </Tabs>
      {error && <Alert severity="error">{error}</Alert>}
      <div
        role="tabpanel"
        id="simulation-panel"
        aria-labelledby="simulation-tab"
        hidden={activeTab !== 'simulation'}
      >
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            {canWrite && (
              <Button
                disabled={busy || !run || loading}
                onClick={() => calculate(true)}
              >
                Przelicz według ustawień magazynu
              </Button>
            )}
            {busy && <CircularProgress size={24} />}
          </Stack>
          <Accordion disableGutters>
            <AccordionSummary expandIcon={<span aria-hidden="true">⌄</span>}>
              <Typography>Ustawienia zakresu i obszaru załadunku</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <SimulationScopePanel
                userRole={userRole}
                run={data.run}
                transports={data.transports ?? []}
                onResult={(result) => {
                  setRunId(result.id);
                  setPage(0);
                  setSearch('');
                  setSearchInput('');
                  setRefresh((v) => v + 1);
                }}
              />
            </AccordionDetails>
          </Accordion>

          {run && !loading && !error && (
            <>
              <details>
                <summary style={{ cursor: 'pointer' }}>
                  Dane użyte w symulacji
                </summary>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  <Typography>
                    Transporty: {run.transportImport.fileName} · magazyn{' '}
                    {run.warehouse} · import{' '}
                    {run.transportImport.createdAt
                      ? new Date(run.transportImport.createdAt).toLocaleString(
                          'pl-PL',
                        )
                      : '—'}
                  </Typography>
                  <Typography>
                    Zapas: {run.stockImport.fileName} · import{' '}
                    {new Date(run.stockImport.createdAt).toLocaleString(
                      'pl-PL',
                    )}
                  </Typography>
                  <Typography>
                    Plan użyty w symulacji:{' '}
                    {run.productionPlan
                      ? `${run.productionPlan.fileName} · import ${new Date(
                          run.productionPlan.createdAt,
                        ).toLocaleString('pl-PL')}`
                      : 'brak'}
                    .
                  </Typography>
                  <Typography>
                    Symulacja wykonana:{' '}
                    {run.createdAt
                      ? new Date(run.createdAt).toLocaleString('pl-PL')
                      : '—'}
                    . Daty importów wskazują wiek danych; nie potwierdzają czasu
                    wykonania eksportu w systemie źródłowym.
                  </Typography>
                </Stack>
              </details>
              {run.dataFreshness &&
                Object.values(run.dataFreshness).some(Boolean) && (
                  <Alert severity="warning">
                    Od wykonania wyniku zaimportowano nowsze dane:{' '}
                    {[
                      run.dataFreshness.stockChanged && 'zapas',
                      run.dataFreshness.productionChanged && 'plan produkcji',
                      run.dataFreshness.transportsChanged && 'transporty',
                    ]
                      .filter(Boolean)
                      .join(', ')}
                    . Ten wynik nie uwzględnia tych importów.
                  </Alert>
                )}
              <Stack direction="row" spacing={1}>
                <Button onClick={() => setRefresh((v) => v + 1)}>
                  Odśwież widok
                </Button>
                {canWrite && ['FAILED', 'PENDING'].includes(run.mailStatus) && (
                  <Button disabled={busy} onClick={retryMail}>
                    Ponów powiadomienie
                  </Button>
                )}
              </Stack>
              {run.mailStatus && (
                <Alert
                  severity={run.mailStatus === 'FAILED' ? 'warning' : 'info'}
                >
                  Powiadomienie: {mailLabels[run.mailStatus] ?? run.mailStatus}.
                  {run.mailError ? ` ${run.mailError}` : ''}
                </Alert>
              )}
              <SimulationReportPanel
                run={run}
                transports={data.transportActions ?? []}
              />
              <Alert
                severity={
                  run.summary.actionableTransports ? 'warning' : 'success'
                }
              >
                Transporty: {run.summary.transports}. Wymagają działania:{' '}
                {run.summary.actionableTransports}. Gotowe pozycje:{' '}
                {run.summary.readyPositions}. Tylko miks:{' '}
                {run.summary.mixOnlyPositions}. Brak danych:{' '}
                {run.summary.missingDataPositions}.
              </Alert>
            </>
          )}
          {!loading && !run && !error && (
            <Alert severity="info">
              Zaimportuj zapas, a następnie plik transportów, aby wykonać
              pierwszą symulację.
              <Button
                onClick={() => {
                  setImportsVisited(true);
                  setActiveTab('imports');
                }}
              >
                Przejdź do importów
              </Button>
            </Alert>
          )}
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            alignItems="center"
          >
            <TextField
              select
              label="Widok transportów"
              value={scopeBucket}
              sx={{ minWidth: 230 }}
              onChange={(e) => {
                setScopeBucket(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">Cały wybrany zakres</MenuItem>
              {Object.entries(BUCKETS).map(([value, label]) => (
                <MenuItem value={value} key={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
            <Autocomplete
              multiple
              disableCloseOnSelect
              disableClearable
              size="small"
              sx={{ minWidth: 280, flex: 1 }}
              options={Object.keys(ACTIONS)}
              value={selectedActions}
              onChange={(_, values) => {
                setSelectedActions(values);
                setPage(0);
              }}
              getOptionLabel={(action) => ACTIONS[action]}
              openText="Wybierz statusy"
              closeText="Zamknij"
              renderOption={(props, action, { selected }) => (
                <li {...props}>
                  <Checkbox checked={selected} tabIndex={-1} sx={{ mr: 1 }} />
                  {ACTIONS[action]}
                </li>
              )}
              renderTags={(values) => (
                <Typography variant="body2" sx={{ mx: 1 }}>
                  {values.length === 1
                    ? ACTIONS[values[0]]
                    : `Wybrane statusy: ${values.length}`}
                </Typography>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Działanie / status"
                  placeholder={
                    selectedActions.length ? '' : 'Brak zaznaczonych statusów'
                  }
                />
              )}
            />
            <Button
              onClick={() => {
                setSelectedActions(Object.keys(ACTIONS));
                setPage(0);
              }}
            >
              Wszystkie statusy
            </Button>
            <Button
              onClick={() => {
                setSelectedActions([]);
                setPage(0);
              }}
            >
              Odznacz wszystkie
            </Button>
            <Button
              onClick={() => {
                setSelectedActions(DEFAULT_ACTIONS);
                setPage(0);
              }}
            >
              Bez dostępnych i tylko miks
            </Button>
            <TextField
              size="small"
              label="Transport, dostawa, pozycja, materiał lub odbiorca"
              value={searchInput}
              sx={{ minWidth: 330 }}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setSearch(searchInput.trim());
                  setPage(0);
                }
              }}
            />
            <Button
              onClick={() => {
                setSearch(searchInput.trim());
                setPage(0);
              }}
            >
              Szukaj
            </Button>
            <Button
              onClick={() => {
                setSearch('');
                setSearchInput('');
                setPage(0);
              }}
            >
              Wyczyść
            </Button>
          </Stack>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" aria-label="Wyniki symulacji">
              <TableHead>
                <TableRow>
                  {[
                    'Transport / załadunek',
                    'Dostawa / pozycja',
                    'Materiał',
                    'Pełne palety / stacki',
                    'Przydzielono',
                    'Niepokryte',
                    'Działanie',
                    'Szczegóły',
                  ].map((label) => (
                    <TableCell key={label}>{label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                )}
                {!loading &&
                  !error &&
                  data.items.map((item) => {
                    const key = `${item.transportNumber}/${item.deliveryNumber}/${item.positionNumber}`;
                    return (
                      <Fragment key={key}>
                        <TableRow>
                          <TableCell>
                            {item.transportNumber}
                            <br />
                            {date(item.loadingDate)}{' '}
                            {item.loadingAppointmentAt?.slice(11, 16) ?? ''}
                            <Typography variant="caption" display="block">
                              Obszar transportu:{' '}
                              {AREAS[item.targetArea] ?? 'do ustalenia'}
                              {item.areaConfirmed ? ' (zatwierdzony)' : ''}
                            </Typography>
                            {item.scopeBucket && (
                              <Typography variant="caption" display="block">
                                {BUCKETS[item.scopeBucket]}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {item.deliveryNumber}
                            <br />
                            {item.positionNumber}
                          </TableCell>
                          <TableCell>
                            {item.materialNumber}
                            <br />
                            {item.description}
                          </TableCell>
                          <TableCell>
                            {item.actions.includes('MISSING_DATA') &&
                            !item.fullQuantity
                              ? '—'
                              : `${number(item.fullQuantity)} ${
                                  item.unit ?? ''
                                }`}
                            <br />
                            {item.palletSize
                              ? `${number(
                                  item.fullQuantity / item.palletSize,
                                )} palet / stacków`
                              : ''}
                          </TableCell>
                          <TableCell>
                            {number(item.allocatedQuantity)} {item.unit}
                          </TableCell>
                          <TableCell>
                            {item.actions.includes('MISSING_DATA')
                              ? 'Nie oceniono'
                              : `${number(item.unfulfilledQuantity)} ${
                                  item.unit ?? ''
                                }`}
                          </TableCell>
                          <TableCell>
                            <Stack spacing={0.5}>
                              {item.actions.map((action) => (
                                <Chip
                                  key={action}
                                  size="small"
                                  label={ACTIONS[action]}
                                  color={
                                    action === 'READY'
                                      ? 'success'
                                      : action === 'MIX_ONLY'
                                      ? 'default'
                                      : 'warning'
                                  }
                                />
                              ))}
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Button
                              onClick={() =>
                                setExpanded(expanded === key ? null : key)
                              }
                            >
                              {expanded === key ? 'Zwiń' : 'Pokaż'}
                            </Button>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={8} sx={{ py: 0 }}>
                            <Collapse in={expanded === key} unmountOnExit>
                              <Stack spacing={1} sx={{ py: 2 }}>
                                {(item.actionDetails ?? item.errors).map(
                                  (action, index) => (
                                    <Typography
                                      key={index}
                                      sx={{
                                        fontSize: '1rem',
                                        fontWeight: index === 0 ? 600 : 400,
                                      }}
                                    >
                                      {action}
                                    </Typography>
                                  ),
                                )}
                                {item.actions.includes('READY') && (
                                  <Typography>Zapas dostępny.</Typography>
                                )}
                                <ShelfLifeAcceptance
                                  item={item}
                                  run={data.run}
                                  canWrite={canWrite}
                                  active={data.activeShelfLifeAcceptances ?? []}
                                  onSaved={(result) => {
                                    setRunId(result.id);
                                    setRefresh((v) => v + 1);
                                  }}
                                />
                                <details>
                                  <summary
                                    style={{
                                      cursor: 'pointer',
                                      padding: '8px 0',
                                    }}
                                  >
                                    Szczegóły zapasu
                                  </summary>
                                  <Stack spacing={1} sx={{ pt: 1 }}>
                                    <Typography>
                                      Ilość: {number(item.sourceQuantity)}{' '}
                                      {item.sourceUnit}. Grupa:{' '}
                                      {item.groupCode ?? '—'}. Obszar:{' '}
                                      {AREAS[item.targetArea] ?? '—'}.
                                    </Typography>
                                    {item.mixQuantity > 0 && (
                                      <Typography>
                                        Część miksowa:{' '}
                                        {number(item.mixQuantity)} {item.unit}.
                                      </Typography>
                                    )}
                                    <ShelfLifeOptions item={item} />
                                    {item.mixPickingOptions?.map((lot) => (
                                      <Typography key={lot.stockId}>
                                        Mix: {number(lot.quantity)} {item.unit}{' '}
                                        — skład {lot.storageLocation},{' '}
                                        {AREAS[lot.area]}, bin {lot.bin}, partia{' '}
                                        {lot.batch ?? '—'}, termin{' '}
                                        {date(lot.expiresAt)}.
                                      </Typography>
                                    ))}
                                    {item.mixPickingQuantity > 0 && (
                                      <Typography variant="body2">
                                        Propozycja mix nie jest rezerwacją
                                        zapasu.
                                      </Typography>
                                    )}
                                    {item.productionOrders?.map((order) => (
                                      <Typography key={order.orderNumber}>
                                        Zlecenie {order.orderNumber}, linia{' '}
                                        {order.line}: {date(order.startsAt)}{' '}
                                        {order.startsAt.slice(11, 16)} — plan{' '}
                                        {number(order.quantity)} {order.unit}.
                                      </Typography>
                                    ))}
                                    {item.productionOrders?.length > 0 && (
                                      <Typography variant="body2">
                                        Produkcja nie gwarantuje dostępności —
                                        możliwe opóźnienie lub awaria.
                                      </Typography>
                                    )}
                                    {item.allocations.map((lot) => (
                                      <Typography key={lot.stockId}>
                                        {number(lot.quantity)} {item.unit} —
                                        skład {lot.storageLocation},{' '}
                                        {AREAS[lot.area]}, bin {lot.bin}, partia{' '}
                                        {lot.batch ?? '—'}, termin{' '}
                                        {date(lot.expiresAt)}.
                                      </Typography>
                                    ))}
                                  </Stack>
                                </details>
                              </Stack>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </Fragment>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={data.total}
            page={page}
            rowsPerPage={limit}
            rowsPerPageOptions={[25, 50, 100]}
            labelRowsPerPage="Pozycji na stronie:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} z ${count}`
            }
            onPageChange={(_, value) => setPage(value)}
            onRowsPerPageChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(0);
            }}
            backIconButtonProps={{ disabled: loading || page === 0 }}
            nextIconButtonProps={{
              disabled: loading || (page + 1) * limit >= data.total,
            }}
          />
        </Stack>
      </div>
      <div
        role="tabpanel"
        id="imports-panel"
        aria-labelledby="imports-tab"
        hidden={activeTab !== 'imports'}
      >
        {importsVisited && (
          <Stack spacing={3}>
            <Accordion disableGutters>
              <AccordionSummary expandIcon={<span aria-hidden="true">⌄</span>}>
                <Typography>Zapas — import i podgląd</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TransportStockPage userRole={userRole} embedded />
              </AccordionDetails>
            </Accordion>
            <Accordion disableGutters>
              <AccordionSummary expandIcon={<span aria-hidden="true">⌄</span>}>
                <Typography>Plan produkcji — import i podgląd</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <ProductionPlanPanel canWrite={canWrite} />
              </AccordionDetails>
            </Accordion>
            {canWrite && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <Typography>
                    Importuj plik transportów. Pozycje z X w kolumnach R, S lub
                    T zostaną pominięte.
                  </Typography>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <input
                      type="file"
                      accept=".xlsx"
                      ref={fileInput}
                      aria-label="Plik transportów"
                      disabled={busy}
                      onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                    />
                    <Button
                      variant="contained"
                      disabled={busy || !file}
                      onClick={() => calculate()}
                    >
                      Importuj i sprawdź
                    </Button>
                  </Stack>
                  {busy && <CircularProgress size={24} />}
                </Stack>
              </Paper>
            )}

            <Typography variant="h6">Historia symulacji</Typography>
            {historyError && <Alert severity="error">{historyError}</Alert>}
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Wykonano</TableCell>
                    <TableCell>Transporty</TableCell>
                    <TableCell>Zapas</TableCell>
                    <TableCell>Tryb / wynik</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        {new Date(item.createdAt).toLocaleString('pl-PL')}
                      </TableCell>
                      <TableCell>{item.transportImport.fileName}</TableCell>
                      <TableCell>{item.stockImport.fileName}</TableCell>
                      <TableCell>
                        {item.mode === 'PREVIEW' ? 'Ręczny podgląd' : 'Bieżąca'}
                        <Button
                          onClick={() => {
                            setRunId(item.id);
                            setActiveTab('simulation');
                            setPage(0);
                          }}
                        >
                          Pokaż
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={history.total}
              page={historyPage}
              rowsPerPage={10}
              rowsPerPageOptions={[10]}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} z ${count}`
              }
              onPageChange={(_, value) => setHistoryPage(value)}
            />
          </Stack>
        )}
      </div>
    </Stack>
  );
}
